import React, { useEffect, useState, useMemo } from "react";
import { Cube, MonitorPlay, Wrench, ArrowUpRight } from "@phosphor-icons/react";
import { api, API } from "@/lib/api";

const AMAZON_AFFILIATE_TAG = "eigoo-20"; // placeholder affiliate tag slot

function amazonSearchUrl(query) {
  const q = encodeURIComponent(query);
  return `https://www.amazon.com/s?k=${q}&tag=${AMAZON_AFFILIATE_TAG}`;
}

function softwareSearchUrl(name) {
  // Best-effort: search for the official site
  const q = encodeURIComponent(`${name} official`);
  return `https://www.google.com/search?q=${q}`;
}

export default function ArsenalPanel({ arsenal }) {
  const hardware = arsenal?.hardware || [];
  const software = arsenal?.software || [];

  // Build the vendor URL list once per render
  const vendorItems = useMemo(() => {
    const items = [];
    hardware.forEach((h, i) =>
      items.push({ vendor_url: amazonSearchUrl(h.name), label: h.name, source: "arsenal_hardware", key: `h-${i}` })
    );
    software.forEach((s, i) =>
      items.push({ vendor_url: softwareSearchUrl(s.name), label: s.name, source: "arsenal_software", key: `s-${i}` })
    );
    return items;
  }, [hardware, software]);

  // key -> tracked /go/<code> URL. Falls back to raw vendor URL until the
  // register call returns (so the UI is never broken).
  const [trackedByKey, setTrackedByKey] = useState({});

  useEffect(() => {
    let cancelled = false;
    if (vendorItems.length === 0) return;

    (async () => {
      try {
        const payload = {
          items: vendorItems.map(({ vendor_url, label, source }) => ({
            vendor_url,
            label,
            source,
          })),
        };
        const { data } = await api.post("/affiliates/register", payload);
        if (cancelled) return;
        const map = {};
        // Response preserves order of input items
        (data.links || []).forEach((link, idx) => {
          const key = vendorItems[idx]?.key;
          if (key && link.code) {
            map[key] = `${API}/go/${link.code}`;
          }
        });
        setTrackedByKey(map);
      } catch {
        // Non-fatal — the raw vendor URL still works as a fallback
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [vendorItems]);

  if (!arsenal) return null;
  if (hardware.length === 0 && software.length === 0) return null;

  const hrefFor = (key, fallback) => trackedByKey[key] || fallback;

  return (
    <div className="surface-card p-6" data-testid="arsenal-panel">
      <div className="flex items-center gap-3 mb-5">
        <Cube size={20} weight="duotone" className="text-[#FFD700]" />
        <h3 className="font-bold uppercase tracking-wider text-sm">Niche Arsenal</h3>
      </div>

      {hardware.length > 0 && (
        <div className="mb-6">
          <div className="overline text-[0.55rem] mb-3 flex items-center gap-2">
            <Wrench size={12} /> Hardware
          </div>
          <div className="space-y-2">
            {hardware.map((h, i) => {
              const key = `h-${i}`;
              const fallback = amazonSearchUrl(h.name);
              return (
                <a
                  key={i}
                  href={hrefFor(key, fallback)}
                  target="_blank"
                  rel="noopener noreferrer sponsored"
                  data-testid={`arsenal-hardware-${i}`}
                  className="block border-l-2 border-[#FFD700]/30 pl-3 py-2 hover:border-[#FFD700] hover:bg-white/5 transition-all group"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-semibold text-sm text-white">{h.name}</div>
                      <div className="text-xs text-white/60 font-light">{h.purpose}</div>
                      <div className="font-mono text-[0.6rem] uppercase tracking-widest text-[#FFD700]/70 mt-1">
                        {h.estimated_cost}{h.where ? ` // ${h.where}` : ""}
                      </div>
                    </div>
                    <ArrowUpRight size={12} className="text-white/30 group-hover:text-[#FFD700] flex-shrink-0 mt-1" />
                  </div>
                </a>
              );
            })}
          </div>
        </div>
      )}

      {software.length > 0 && (
        <div>
          <div className="overline text-[0.55rem] mb-3 flex items-center gap-2">
            <MonitorPlay size={12} /> Software
          </div>
          <div className="space-y-2">
            {software.map((s, i) => {
              const key = `s-${i}`;
              const fallback = softwareSearchUrl(s.name);
              return (
                <a
                  key={i}
                  href={hrefFor(key, fallback)}
                  target="_blank"
                  rel="noopener noreferrer sponsored"
                  data-testid={`arsenal-software-${i}`}
                  className="block border-l-2 border-[#FFD700]/30 pl-3 py-2 hover:border-[#FFD700] hover:bg-white/5 transition-all group"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-semibold text-sm text-white">{s.name}</div>
                      <div className="text-xs text-white/60 font-light">{s.purpose}</div>
                      <div className="font-mono text-[0.6rem] uppercase tracking-widest text-[#FFD700]/70 mt-1">
                        {s.monthly_cost}{s.category ? ` // ${s.category}` : ""}
                      </div>
                    </div>
                    <ArrowUpRight size={12} className="text-white/30 group-hover:text-[#FFD700] flex-shrink-0 mt-1" />
                  </div>
                </a>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
